/**
 * @file CustomerPickerDialog.tsx
 * @description Боковая панель выбора клиента (phase10).
 *
 * Открывается справа (Sheet side=right). Поиск + список.
 * Клик на клиента → onSelect + закрытие.
 */

import { Loader2, Search, User } from "lucide-react";
import { useEffect, useState } from "react";

import { Input } from "../ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { customers, type CustomerDto } from "../../lib/api-customers";

interface Props {
  onClose: () => void;
  onSelect: (c: CustomerDto) => void;
}

export function CustomerPickerDialog({ onClose, onSelect }: Props) {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<CustomerDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await customers.list({
          search: search.trim() || undefined,
          limit: 100,
        });
        if (!cancelled) setItems(res.items);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [search]);

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md flex flex-col p-0 gap-0"
      >
        <SheetHeader className="px-6 pt-6 pb-3 border-b border-on-surface/8">
          <SheetTitle>Выбор клиента</SheetTitle>
        </SheetHeader>

        <div className="px-6 py-3 border-b border-on-surface/8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-on-surface-variant" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Имя, email или телефон"
              className="pl-9"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-2">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="size-6 animate-spin text-on-surface-variant" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-on-surface-variant text-sm">
              Клиентов не найдено
            </div>
          ) : (
            <ul className="flex flex-col gap-1">
              {items.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center gap-3 p-2 rounded hover:bg-surface/40 cursor-pointer"
                  onClick={() => onSelect(c)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelect(c);
                    }
                  }}
                >
                  <div className="size-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <User className="size-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {c.displayName ?? "—"}
                    </div>
                    <div className="text-xs text-on-surface-variant truncate">
                      {c.email ?? c.phone ?? "—"}
                      {c.type === "LEGAL" ? " · Юр.лицо" : ""}
                    </div>
                  </div>
                  {c.ordersCount > 0 ? (
                    <span className="text-xs text-on-surface-variant shrink-0">
                      {c.ordersCount} зак.
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
